import type { ExtractedEvent } from '../types';
import {
  calendarDateIndex,
  eventDateKey,
  formatDateLabel,
  formatEventClock,
  formatEventDate,
  formatEventRange,
  instantToEventDateTime,
  monthOnlyLabel,
  type EventDateFormatOptions,
  type EventRangeFormatOptions,
  type EventTemporalInput,
} from './eventTemporal';

/**
 * Short, human-readable date label for an event's start (e.g. "Sat, Apr 27").
 *
 * Used in the compact event-row time pill so ungrouped lists still show which
 * day each event falls on. Shared by HomePage's inline renderer and the
 * `EventList` component so both surfaces stay in lockstep (pitfall #13).
 */
export function shortDateLabel(event: EventTemporalInput): string {
  const input = temporalInput(event);
  // A month-only span reads "March 2027", not its first day (#67).
  return monthOnlyLabel(input) ?? formatEventDate(input) ?? 'Invalid date';
}

/**
 * True when an ISO 8601 timestamp's clock portion is exactly midnight,
 * irrespective of the trailing timezone designator. Mirrors the server-side
 * heuristic in `api/src/functions/events-published.ts` — kept here as a
 * defensive fallback so the UI stays correct even on rows where the API
 * forgets to project `isAllDay`.
 */
function isMidnightLocal(iso: string): boolean {
  return /T00:00:00(?:\.0+)?(?:Z|[+-]\d{2}:?\d{2})?$/.test(iso);
}

/**
 * Resolves the all-day flag from the API field with a midnight fallback.
 *
 * Almost no real events start at exactly 12:00 AM — when an extractor lands
 * a midnight startUtc it nearly always means "the LLM had no specific time".
 * Treat such rows as all-day UNLESS there is an explicit non-midnight end
 * time (e.g. a 12:00 AM → 02:00 AM late-night show).
 */
export function isAllDayEvent(e: EventTemporalInput): boolean {
  if (typeof e.isAllDay === 'boolean') return e.isAllDay;
  if (!isMidnightLocal(e.startUtc)) return false;
  return !e.endUtc || isMidnightLocal(e.endUtc);
}

/**
 * New York calendar-day key (`yyyy-MM-dd`) for an event.
 *
 * For all-day events we use the date *as written* in the ISO string so a
 * `2026-04-27T00:00:00-04:00` event is bucketed on April 27 regardless of
 * the viewer's timezone. For timed events we use the New York
 * calendar day of the parsed instant, even when the viewer is elsewhere.
 * Invalid dates have no bucket; never manufacture today's date for them.
 */
export function eventDayKey(e: EventTemporalInput): string | null {
  return eventDateKey(temporalInput(e));
}

/** Legacy all-day inference and empty optional ends stay outside the pure contract. */
function temporalInput(event: EventTemporalInput): EventTemporalInput {
  return { ...event, endUtc: event.endUtc || undefined, isAllDay: isAllDayEvent(event) };
}

/** The event calendar's current date, never the browser's current date. */
export function eventTodayKey(now: Date = new Date()): string {
  if (!Number.isFinite(now.getTime())) throw new RangeError('Invalid reference instant.');
  const key = eventDateKey({ startUtc: now.toISOString(), isAllDay: false });
  if (!key) throw new RangeError('Reference instant is outside the event calendar.');
  return key;
}

/** One date field for date tiles, weekdays and calendar navigation labels. */
export function eventDatePart(event: EventTemporalInput, options: EventDateFormatOptions): string {
  return formatEventDate(temporalInput(event), {
    weekday: undefined, month: undefined, day: undefined, ...options,
  }) ?? '—';
}

/**
 * The three parts of an image-less date tile: weekday, day and month.
 *
 * A normal event gets exactly the `eventDatePart` values the tiles have always shown ("Fri", "6", "Nov").
 * A month-only event — one the source dated no more precisely than "March 2027" — gets no weekday, its
 * short month where the day goes, and the four-digit year where the month goes (#67). The tile then reads
 * "MAR 2027" instead of the weekday and day 1 of a month nobody announced. All three come from the stored
 * start's calendar date, and an empty `weekday` means the tile renders no weekday line.
 */
export function dateTileParts(event: EventTemporalInput): { weekday: string; day: string; month: string } {
  const input = temporalInput(event);
  if (monthOnlyLabel(input) !== null) {
    return {
      weekday: '',
      day: eventDatePart(input, { month: 'short' }),
      month: eventDatePart(input, { year: 'numeric' }),
    };
  }
  return {
    weekday: eventDatePart(input, { weekday: 'short' }),
    day: eventDatePart(input, { day: 'numeric' }),
    month: eventDatePart(input, { month: 'short' }),
  };
}

/** Shared visible and accessible range label for lists, maps and review pages. */
export function formatRange(event: EventTemporalInput, options?: EventRangeFormatOptions): string {
  return formatEventRange(temporalInput(event), options) ?? 'Invalid date/time';
}

/** Shared compact time tile. */
export function timePillParts(event: EventTemporalInput): { start: string; end?: string } {
  const input = temporalInput(event);
  const invalid = { start: 'Invalid date/time' };
  if (input.isAllDay) {
    const first = eventDateKey(input);
    const last = input.endUtc == null ? first : eventDateKey({ ...input, startUtc: input.endUtc });
    return first && last && last >= first ? { start: formatEventClock(input)! } : invalid;
  }
  const first = instantToEventDateTime(input.startUtc);
  const last = input.endUtc == null ? first : instantToEventDateTime(input.endUtc);
  if (!first.ok || !last.ok || Date.parse(last.value.isoUtc) < Date.parse(first.value.isoUtc)) return invalid;
  const start = formatEventClock(input)!;
  if (first.value.isoUtc === last.value.isoUtc) return { start };
  // Distinct instants imply an end was supplied and validated above.
  const endUtc = input.endUtc!;
  const endEvent = { startUtc: endUtc, isAllDay: false };
  const endClock = formatEventClock(endEvent)!;
  if (first.value.dateKey !== last.value.dateKey) {
    return { start, end: `${shortDateLabel(endEvent)} · ${endClock}` };
  }
  if (first.value.offset !== last.value.offset) {
    return {
      start: `${start} (UTC${first.value.offset})`,
      end: `${endClock} (UTC${last.value.offset})`,
    };
  }
  return { start, end: endClock };
}

/** Calendar-date headers and relative labels use whole days, not 24h durations. */
export function formatDateHeader(dayKey: string, now: Date = new Date()): string {
  const index = calendarDateIndex(dayKey);
  if (index === null) return 'Invalid date';
  const today = eventTodayKey(now);
  const diffDays = index - calendarDateIndex(today)!;
  const long = formatDateLabel(dayKey, {
    weekday: 'long', month: 'long', day: 'numeric',
    year: dayKey.slice(0, 4) === today.slice(0, 4) ? undefined : 'numeric',
  })!;
  if (diffDays === 0) return `Today · ${long}`;
  if (diffDays === 1) return `Tomorrow · ${long}`;
  return long;
}

/** Chronological date sort with all-day entries first and invalid dates last. */
export function compareEventDates(a: EventTemporalInput, b: EventTemporalInput, dir: 1 | -1 = 1): number {
  const dayA = eventDayKey(a), dayB = eventDayKey(b);
  if (dayA === null || dayB === null) {
    return dayA === dayB ? 0 : dayA === null ? 1 : -1;
  }
  if (dayA !== dayB) return dir * dayA.localeCompare(dayB);
  const aAll = isAllDayEvent(a), bAll = isAllDayEvent(b);
  if (aAll !== bAll) return aAll ? -1 : 1;
  const tA = Date.parse(a.startUtc), tB = Date.parse(b.startUtc);
  return Number.isFinite(tA) && Number.isFinite(tB)
    ? dir * (tA - tB)
    : dir * a.startUtc.localeCompare(b.startUtc);
}

/**
 * True when this row is an auto-extracted recurring occurrence whose source
 * post is materially older than the occurrence date — i.e. it was projected
 * forward by the recurrence expansion and has NOT been refreshed by a more
 * recent post about the same date.
 *
 * `postCreatedAt` reflects the most-recent-post date after pre-write dedup
 * (`mergeIntoKeeper({promotePrimaryByPostDate:true})`), so an instance that
 * is stale-projected keeps the original anchor's old `postCreatedAt`. A
 * "real" occurrence — one a fresh post explicitly mentioned — gets bumped
 * to the new post's date.
 *
 * Manually-flagged permanent series (`recurringIsPermanent`) and series that
 * are still within their normal window are not flagged.
 */
const STALE_RECURRING_PROJECTION_DAYS = 21;
export function isStaleRecurringProjection(
  e: Pick<
    ExtractedEvent,
    'isRecurringInstance' | 'recurringIsPermanent' | 'recurringPattern' | 'startUtc' | 'postCreatedAt'
  >
): boolean {
  if (e.recurringIsPermanent) return false;
  if (!e.isRecurringInstance) return false;
  if (!e.recurringPattern || e.recurringPattern === 'one-time') return false;
  if (!e.postCreatedAt) return false;
  const start = Date.parse(e.startUtc);
  const posted = Date.parse(e.postCreatedAt);
  if (!Number.isFinite(start) || !Number.isFinite(posted)) return false;
  return start - posted > STALE_RECURRING_PROJECTION_DAYS * 86_400_000;
}
